import { useQuery } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { announcementsApi } from '@/services/api/endpoints/notifications';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDateTime } from '@/utils/format';

/**
 * The reader's notice board.
 *
 * <p>Everything here comes from /announcements/feed, resolved on the server
 * from the signed-in account — its roles, a student's own section, a parent's
 * children's sections. This page sends no audience and filters nothing: what
 * arrives is what this person should see, already newest first.
 *
 * <p>The chips say how far a notice reaches — the whole school, one campus, or
 * one class — so a parent can tell a school-wide closure from a note to their
 * child's section at a glance.
 */
export function MyAnnouncementsPage() {
  const feed = useQuery({
    queryKey: queryKeys.announcements.feed,
    queryFn: announcementsApi.feed,
  });

  return (
    <div>
      <PageHeader
        title="Announcements"
        description="Notices from the school for you, newest first."
        icon={<Megaphone className="h-5 w-5" aria-hidden="true" />}
      />

      {feed.isPending ? (
        <Card>
          <LoadingState label="Loading announcements" />
        </Card>
      ) : feed.isError ? (
        <Card>
          <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
        </Card>
      ) : feed.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Megaphone className="h-6 w-6" aria-hidden="true" />}
            title="No announcements"
            description="Nothing is posted for you right now. New notices will appear here."
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {feed.data.map((announcement) => (
            <li key={announcement.id}>
              <Card>
                <article className="px-4 py-4 sm:px-5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={announcement.campusId === undefined ? 'accent' : 'info'}>
                      {announcement.campusName ?? 'Whole school'}
                    </Badge>
                    {announcement.sectionName !== undefined && (
                      <Badge tone="neutral">{announcement.sectionName}</Badge>
                    )}
                  </div>
                  <h2 className="mt-2 text-base font-bold text-ink">{announcement.title}</h2>
                  <p className="mt-0.5 text-[11px] text-subtle">
                    <time dateTime={announcement.publishedAt}>
                      {formatDateTime(announcement.publishedAt)}
                    </time>
                    {announcement.expiresAt !== undefined && (
                      <> · Until {formatDateTime(announcement.expiresAt)}</>
                    )}
                  </p>
                  <p className="mt-3 whitespace-pre-line break-words text-sm text-ink">
                    {announcement.body}
                  </p>
                </article>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
